import React, { useEffect, useState } from "react";
import { BasicUserType } from "@/types/user";
// Socket functionality removed - using API calls only
import { useToast } from "@/components/ui/use-toast";
import { getPost, repostPost } from "@/actions/posts/index";
import notify from "@/lib/notify";
import { apiClient } from "@/lib/api-client";

interface PostInteractionsProps {
  id: string;
  userId: string;
  currentUser?: BasicUserType | null;
  initialLikes: string[];
  initialReposts: string[];
  onInteractionChange: (type: 'like' | 'repost' | 'bookmark', count: number, isActive: boolean) => void;
  onRepost?: (repostedPost: any) => void;
}

export function PostInteractions({
  id,
  userId,
  currentUser,
  initialLikes,
  initialReposts,
  onInteractionChange,
  onRepost,
}: PostInteractionsProps) {
  const [isLiked, setIsLiked] = useState(currentUser ? (initialLikes || []).includes(currentUser.id) : false);
  const [likeCount, setLikeCount] = useState(initialLikes?.length || 0);
  const [isReposted, setIsReposted] = useState(currentUser ? (initialReposts || []).includes(currentUser.id) : false);
  const [repostCount, setRepostCount] = useState(initialReposts?.length || 0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const { toast } = useToast();
  // Socket functionality removed - using API calls only

  useEffect(() => {
    const checkBookmarkStatus = async () => {
      if (!currentUser) return;
      try {
        // Use API client to check bookmark status
        const data = await apiClient.getBookmarkStatus(id);
        setIsBookmarked(data.isBookmarked || false);
        setBookmarkCount(data.bookmarkCount || 0);
      } catch (error) {
        console.error('Error checking bookmark status:', error);
        // Fallback: don't make any API calls, just use initial state
        setIsBookmarked(false);
        setBookmarkCount(0);
      }
    };

    // Only check bookmark status if we don't have initial data
    // For now, let's disable this automatic check to prevent excessive API calls
    // checkBookmarkStatus();
  }, [currentUser, id]);

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      toast({
        title: "Error",
        description: "Please log in to like posts",
        variant: "destructive",
      });
      return;
    }

    const wasLiked = isLiked;
    const originalCount = likeCount;

    try {
      // Optimistically update UI
      const newIsLiked = !wasLiked;
      const newLikeCount = wasLiked ? likeCount - 1 : likeCount + 1;
      
      setIsLiked(newIsLiked);
      setLikeCount(newLikeCount);
      onInteractionChange('like', newLikeCount, newIsLiked);

      // Make API call to toggle like using API client
      if (wasLiked) {
        await apiClient.unlikePost(id);
      } else {
        await apiClient.likePost(id);
      }

      // Send notification for new likes
      if (newIsLiked && userId !== currentUser.id) {
        notify({
          userId: userId,
          type: "like",
          message: `${currentUser.name} liked your post`,
        });
      }
    } catch (error) {
      console.error('Error handling like:', error);
      // Revert optimistic update on failure - go back to original state
      setIsLiked(wasLiked);
      setLikeCount(originalCount);
      onInteractionChange('like', originalCount, wasLiked);
      
      toast({
        title: "Error",
        description: "Failed to like post",
        variant: "destructive",
      });
    }
  };

  const handleRepost = async () => {
    if (!currentUser) {
      toast({
        title: "Error",
        description: "Please log in to repost",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await apiClient.repostPost(id);
      // Backend now returns { post: repostPost }
      if (response && response.post) {
        toast({
          title: "Success",
          description: "Post reposted!",
        });
        const newRepostCount = repostCount + 1;
        setRepostCount(newRepostCount);
        setIsReposted(true);
        onInteractionChange('repost', newRepostCount, true);
        
        // Add the reposted post to the feed
        if (onRepost) {
          onRepost({ repostedPost: response.post });
        }
      }
    } catch (error) {
      console.error("Error reposting:", error);
      toast({
        title: "Error",
        description: "Failed to repost",
        variant: "destructive",
      });
    }
  };

  const handleBookmark = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentUser) {
      toast({
        title: "Error",
        description: "Please log in to bookmark posts",
        variant: "destructive",
      });
      return;
    }

    try {
      if (isBookmarked) {
        await apiClient.removeBookmark(id);
        setIsBookmarked(false);
        const newCount = bookmarkCount - 1;
        setBookmarkCount(newCount);
        onInteractionChange('bookmark', newCount, false);
        toast({
          title: "Success",
          description: "Post removed from bookmarks",
        });
      } else {
        await apiClient.addBookmark(id);
        setIsBookmarked(true);
        const newCount = bookmarkCount + 1;
        setBookmarkCount(newCount);
        onInteractionChange('bookmark', newCount, true);
        toast({
          title: "Success",
          description: "Post added to bookmarks",
        });
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      toast({
        title: "Error",
        description: "Failed to update bookmark",
        variant: "destructive",
      });
    }
  };

  return {
    isLiked,
    likeCount,
    isReposted,
    repostCount,
    isBookmarked,
    bookmarkCount,
    handleLike,
    handleRepost,
    handleBookmark,
  };
}